import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { pickFiles } from "@salestrak/ui";
import {
  AddIssueAttachmentsDocument,
  RemoveIssueAttachmentDocument,
  RenameIssueMediaDocument,
} from "#app/lib/data";
import { usePendingMediaUploads } from "#app/lib/hooks/use-pending-media-uploads";
import { NOTE_MEDIA_ACCEPT } from "#app/lib/media-kind";
import { toastMutationError } from "#app/lib/utils/toast-errors";

// Uploads attach in ONE write, so a later response can never land before an earlier one.
export function useIssueAttachments(issueId: string) {
  const [addAttachments] = useMutation(AddIssueAttachmentsDocument);
  const [removeAttachment] = useMutation(RemoveIssueAttachmentDocument);
  const [renameMedia] = useMutation(RenameIssueMediaDocument);
  const { pending, startUploads } = usePendingMediaUploads({ onUploaded: () => {} });
  const [removing, setRemoving] = useState(false);

  const upload = useCallback(async () => {
    const uploaded = await startUploads(await pickFiles(NOTE_MEDIA_ACCEPT, true));
    if (!uploaded.length) return;
    try {
      await addAttachments({
        variables: { issueId, mediaIds: uploaded.map((media) => media.photoId) },
      });
    } catch (err) {
      toastMutationError(err, "Failed to add attachments");
    }
  }, [addAttachments, issueId, startUploads]);

  const remove = useCallback(
    async (mediaId: string) => {
      setRemoving(true);
      try {
        await removeAttachment({ variables: { issueId, mediaId } });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to delete attachment");
        return false;
      } finally {
        setRemoving(false);
      }
    },
    [issueId, removeAttachment],
  );

  const rename = useCallback(
    async (mediaId: string, name: string) => {
      const next = name.trim() || null;
      try {
        await renameMedia({
          variables: { issueId, mediaId, name: next },
          optimisticResponse: { renameIssueMedia: { __typename: "Media", id: mediaId, name: next } },
        });
      } catch (err) {
        toastMutationError(err, "Failed to rename file");
      }
    },
    [issueId, renameMedia],
  );

  return { upload, uploading: pending.length, remove, removing, rename };
}
